import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import get from 'lodash.get';
import {
    withStyles,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Grid,
    Typography,
    Button,
    List,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Chip,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import CancelPresentation from '@material-ui/icons/CancelPresentation';

import { GUIDE_SUB_TYPE } from 'yeself-common/ModelConstant';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectContextHelp,
    selectEnabledContextHelpTours,
    selectSystemPagesV2Enabled,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormSelect from 'Console/components/common/form/FormSelect';

import { selectContentLanguageCode, selectContentLanguage } from 'Console/data/language/selectors';
import { selectApplicationTours } from 'Console/data/applicationDetail/guides/selectors';
import { updateTour } from 'Console/data/applicationDetail/guides/actions';
import { getValueFromLocalizedObject } from 'Console/utils/languageUtils';

import GuideSettingsDialog from './GuideSettingsDialog';
import { changeContextHelpListOrder } from './tourExpansionPanelOrdering';

const styles = (theme) => ({
    root: {
        boxShadow: '0px 1px 0px rgba(0, 0, 0, 0.25)',
    },
    column: {
        flexBasis: '45%',
    },
    btnAdd: {
        marginTop: theme.spacing.unit + 7,
        marginLeft: theme.spacing.unit * 5 + 7,
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
        },
    },
    listItems: {
        paddingTop: theme.spacing.unit * 2 + 4,
        paddingBottom: theme.spacing.unit * 2 + 4,
    },
    listItem: {
        padding: 0,
    },
    listItemIcon: {
        margin: 0,
    },
    boxList: {
        width: '100%',
        maxWidth: '960px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    chips: {
        textTransform: 'uppercase',
        display: 'flex',
        flexWrap: 'wrap',
        flexShrink: 0,
    },
    chip: {
        height: theme.typography.pxToRem(22),
        fontSize: theme.typography.pxToRem(9),
        marginRight: theme.typography.pxToRem(10),
        marginBottom: theme.typography.pxToRem(5),
        fontWeight: 600,
        letterSpacing: '0.0357143em',
    },
    itemsBox: {
        width: '100%',
    },
    details: {
        paddingTop: theme.typography.pxToRem(24),
    },
});

const TourExpansionPanel = ({
    form: { getFieldValue, setFieldsValue },
    intl,
    classes,
    form,
    tours = [],
    contentLanguageCode,
    enabledTours,
    updateDetailSettings,
    updateTour,
    contextHelp,
    contentLanguage,
    isPlaylistSubType = false,
    systemPagesV2Enabled,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editGuideOpen, setEditGuideOpen] = useState(null);

    const shouldIncludeTourBasedOnSubType = (subType) =>
        isPlaylistSubType ? subType === GUIDE_SUB_TYPE.PLAYLIST : subType !== GUIDE_SUB_TYPE.PLAYLIST;

    const selectFormKey = isPlaylistSubType ? 'selectPlaylist' : 'selectTour';

    const filteredToursBySubType = tours.filter(({ subType }) =>
        shouldIncludeTourBasedOnSubType(subType)
    );

    // This line cleans contextHelp.list of tours which no longer exists
    // or are not enabled in context help anymore. *shrug*
    const cleanedContextHelpList = contextHelp.list.filter((guideId) =>
        enabledTours.some(
            ({ id, subType }) => id === guideId && shouldIncludeTourBasedOnSubType(subType)
        )
    );

    // These are tours from enabled tours which are not yet in contextHelp.list
    const newContextHelpListItems = enabledTours
        .filter(
            ({ id, subType }) =>
                shouldIncludeTourBasedOnSubType(subType) &&
                !cleanedContextHelpList.some((guideId) => guideId === id)
        )
        .map(({ id: guideId }) => guideId);

    // The context help list we work with is this:
    // * first come guides from  applications contextHelp.list ordered
    // * after this, guides with enabled contextHelp not present in contextHelp.list
    const contextHelpList = [...cleanedContextHelpList, ...newContextHelpListItems];

    const selectedToursInOrder = contextHelpList.map((guideId) =>
        enabledTours.find((guide) => guide.id === guideId)
    );

    const handleChangeOrder = (guide, value, action) => () => {
        const newContextHelpList = changeContextHelpListOrder(contextHelpList, {
            guide,
            action,
            direction: value,
        });

        updateDetailSettings(
            {
                'metadata.contextHelp.list': newContextHelpList,
            },
            {
                success: `${intl.formatMessage({ id: 'Notification.save' })}`,
                fail: `${intl.formatMessage({ id: 'Notification.fail' })}`,
            }
        );
    };

    const handleAddItem = () => {
        const selectedGuide = filteredToursBySubType.find(
            (guide) => getFieldValue(selectFormKey).id === guide.id
        );

        setFieldsValue({
            [selectFormKey]: '',
        });

        updateTour(
            selectedGuide,
            {
                'metadata.contextHelp.enabled': true,
            },
            {
                success: `${intl.formatMessage({ id: 'Notification.guides.update.success' })}`,
                fail: `${intl.formatMessage({ id: 'Notification.guides.update.fail' })}`,
            }
        );

        handleChangeOrder(selectedGuide, 0, 'add')();
    };

    const handleRemoveItem = (guideToRemove) => () => {
        updateTour(
            guideToRemove,
            {
                'metadata.contextHelp.enabled': false,
            },
            {
                success: `${intl.formatMessage({ id: 'Notification.guides.update.success' })}`,
                fail: `${intl.formatMessage({ id: 'Notification.guides.update.fail' })}`,
            }
        );

        handleChangeOrder(guideToRemove, 0, 'remove')();
    };

    const handleUpdateTourSettings = (tour, data) => {
        updateTour(
            tour,
            {
                'metadata.contextHelp.matchElement': data.matchElement,
                'metadata.contextHelp.matchUrl': data.matchUrl,
                'metadata.contextHelp.pattern': data.pattern,
            },
            {
                success: `${intl.formatMessage({ id: 'Notification.guides.update.success' })}`,
                fail: `${intl.formatMessage({ id: 'Notification.guides.update.fail' })}`,
            }
        );
    };

    if (filteredToursBySubType.length === 0) {
        return null;
    }

    return (
        <ExpansionPanel expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
            <ExpansionPanelSummary className={classes.root} expandIcon={<ExpandMoreIcon />}>
                <div className={classes.column}>
                    <Typography variant="subtitle1">
                        {intl.formatMessage({
                            id: isPlaylistSubType
                                ? 'ContextHelp.expansionPanel.playlists'
                                : 'ContextHelp.expansionPanel.tours',
                        })}
                    </Typography>
                </div>
            </ExpansionPanelSummary>

            <ExpansionPanelDetails className={classes.details}>
                <Grid item xs={12}>
                    <Grid container direction="row" justify="space-between" className={classes.itemsBox}>
                        <Grid style={{ width: '20.5625rem' }}>
                            <FormSelect
                                form={form}
                                fullWidth
                                isRequired
                                formItemKey={selectFormKey}
                                label={intl.formatMessage({
                                    id: isPlaylistSubType
                                        ? 'ContextHelp.playlist.placeholder'
                                        : 'ContextHelp.tour.placeholder',
                                })}
                                optionList={filteredToursBySubType
                                    .filter(
                                        (guide) =>
                                            !selectedToursInOrder.find((tour) => tour.id === guide.id)
                                    )
                                    .map((guide) => ({
                                        id: guide.id,
                                        name: getValueFromLocalizedObject(
                                            guide.name,
                                            contentLanguage.code
                                        ),
                                    }))}
                            />
                        </Grid>

                        <Typography>
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={handleAddItem}
                                className={classes.btnAdd}
                                disabled={!getFieldValue(selectFormKey)}
                            >
                                {intl.formatMessage({ id: 'Buttons.add' })}
                            </Button>
                        </Typography>
                    </Grid>

                    <Grid item className={classes.boxList}>
                        <List className={classes.listItems}>
                            {selectedToursInOrder.map((guide, index) => {
                                const urlMatch = get(guide, 'graph.metadata.contextHelp.matchUrl');
                                const elementPresent = get(
                                    guide,
                                    'graph.metadata.contextHelp.matchElement'
                                );

                                return (
                                    <ListItem className={classes.listItem} key={guide.id}>
                                        <ListItemText
                                            primary={getValueFromLocalizedObject(
                                                guide.name,
                                                contentLanguage.code
                                            )}
                                            primaryTypographyProps={{
                                                style: {
                                                    textWrap: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                },
                                            }}
                                        />
                                        <Grid id="yeself-guide-rule-chips" className={classes.chips}>
                                            {!systemPagesV2Enabled && (
                                                <Chip
                                                    className={classes.chip}
                                                    label="url match"
                                                    color="secondary"
                                                    variant={urlMatch ? 'default' : 'outlined'}
                                                />
                                            )}
                                            <Chip
                                                className={classes.chip}
                                                label="element present"
                                                color="secondary"
                                                variant={elementPresent ? 'default' : 'outlined'}
                                            />
                                        </Grid>

                                        <ListItemIcon className={classes.listItemIcon}>
                                            <IconButton
                                                aria-label="ArrowUpward"
                                                onClick={handleChangeOrder(guide, -1)}
                                                disabled={index === 0}
                                            >
                                                <ArrowUpward />
                                            </IconButton>
                                        </ListItemIcon>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <IconButton
                                                aria-label="ArrowDownward"
                                                onClick={handleChangeOrder(guide, 1)}
                                                disabled={index === selectedToursInOrder.length - 1}
                                            >
                                                <ArrowDownward />
                                            </IconButton>
                                        </ListItemIcon>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <IconButton
                                                aria-label="Settings"
                                                onClick={() => setEditGuideOpen(guide)}
                                            >
                                                <SettingsOutlined />
                                            </IconButton>
                                        </ListItemIcon>
                                        <ListItemIcon className={classes.listItemIcon}>
                                            <IconButton
                                                aria-label="Delete"
                                                onClick={handleRemoveItem(guide)}
                                            >
                                                <CancelPresentation />
                                            </IconButton>
                                        </ListItemIcon>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Grid>
                </Grid>

                {editGuideOpen && (
                    <GuideSettingsDialog
                        closeEditGuide={() => {
                            setEditGuideOpen(null);
                        }}
                        intl={intl}
                        guide={editGuideOpen}
                        contentLanguageCode={contentLanguageCode}
                        onSave={handleUpdateTourSettings}
                    />
                )}
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};

export default connect(
    (state) => ({
        tours: selectApplicationTours(state),
        contentLanguageCode: selectContentLanguageCode(state),
        contentLanguage: selectContentLanguage(state),
        contextHelp: selectContextHelp(state),
        enabledTours: selectEnabledContextHelpTours(state),
        systemPagesV2Enabled: selectSystemPagesV2Enabled(state),
    }),
    { updateDetailSettings, updateTour }
)(withStyles(styles)(injectIntl(TourExpansionPanel)));
